import { forwardRef, useState, useEffect, useRef } from "react";
import {
  getPlans,
  updateCompany,
  getCompany,
  complianceCountryForIso,
  complianceCountryChangeRefusal,
  toComplianceCountry,
  ComplianceCountryChange,
  getStatutoryCountriesToCopy,
} from "../../../api/General/Company";
import {
  SubscriptionPlan,
  CompanyUpdateInput,
  ComplianceCountry,
  CountryView,
  CompanyView,
  TimeZoneInfoView,
  Roles,
} from "../../../models/general";
import { getCountries, getTimeZones } from "../../../api/General/Country";
import { useTranslation } from "react-i18next";
import { getRoles } from "../../../api/General/account";
import { generatePalette, setCSSColorVariables } from "../../../utils";
import PhoneInput from 'react-phone-number-input';
import { useSelector, useDispatch } from "react-redux";
import { setCompanyPrimaryColor, updateCompanyRules } from "../../../store/redux/actions/userActionCreator";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import Select from "../../../components/ui/Select";
import { Checkbox } from "../../../components/ui/Checkbox";
import { Alert, AlertTitle, AlertDescription } from "../../../components/ui/Alert";
import { Spinner } from "../../../components/ui/Spinner";
import { PageLayout } from "../../../components/ui/PageLayout";
import { Building2 } from "lucide-react";
import { useComplianceCountry } from "../../../hooks/useComplianceCountry";
import { useMySubscription } from "../../../hooks/useMySubscription";

// CO-879 - the name a user reads for each compliance country. One literal t() call per value so the
// i18n audit can see every key.
function usePayrollRulesLabel() {
  const { t } = useTranslation();
  return (country: ComplianceCountry): string => {
    switch (country) {
      case "UAE":
        return t("settings.general.countryChange.regime.UAE", "UAE rules");
      case "KSA":
        return t("settings.general.countryChange.regime.KSA", "Saudi Arabia rules");
      default:
        return t("settings.general.countryChange.regime.None", "general rules (no country-specific law)");
    }
  };
}

export const COMPLIANCE_COUNTRY_CHANGE_ACCEPT_ID = "compliance-country-change-accept";

export interface ComplianceCountryChangeNoticeProps {
  /** The compliance country the company runs on today. */
  from: ComplianceCountry;
  /** The one it would run on after the save. */
  to: ComplianceCountry;
  accepted: boolean;
  onAcceptedChange: (accepted: boolean) => void;
  /** A save was blocked, or refused by the server, for want of this confirmation. */
  refused: boolean;
  /** Puts the stored country back. Leave out when the country select was not changed. */
  onKeepCurrent?: () => void;
  /** Name of the stored country, for the "keep" button. */
  keepCountryName?: string;
  /**
   * Name of the country the server holds, when a refusal showed it was changed elsewhere after this
   * screen loaded. Adds one line saying so, because "from" and the keep button then name a country
   * the user never saw on this page.
   */
  changedElsewhereCountryName?: string;
  /**
   * ST-DIALOG - the line saying the new country's statutory leave minimums will be added to the
   * company's Statutory Templates (spec section 5.2). Translated by the caller, whose wording depends on
   * who is reading (Hr: "your company's"; Operator: "this company's"). Leave out when the new country
   * adds no copies. Information only: the one checkbox still confirms the whole change.
   */
  statutoryCopiesLine?: string;
  disabled?: boolean;
  className?: string;
}

/**
 * CO-879 - the consent a compliance-country change needs, shown INLINE under the Country select (a
 * modal is the wrong tool: the user has to read this next to the field that caused it).
 *
 * A company's country decides which statutory payroll rules it runs on (None / UAE / KSA). Until
 * CO-879 any save that carried a different country switched them with nothing but an audit row; the
 * server now refuses such a save unless it carries an explicit confirmation. This block is where the
 * user gives it. Shared by Settings > General (Hr / Admin) and the Operator company screen.
 *
 * Show it only when the user CHANGED the country to one on other rules, or when the server refused
 * a save. A country nobody touched never needs it (see ComplianceCountryOptInNotice for that case).
 *
 * The ref is the checkbox, so a caller can move focus to it when a save is refused.
 */
export const ComplianceCountryChangeNotice = forwardRef<HTMLInputElement, ComplianceCountryChangeNoticeProps>(
  function ComplianceCountryChangeNotice(
    {
      from,
      to,
      accepted,
      onAcceptedChange,
      refused,
      onKeepCurrent,
      keepCountryName,
      changedElsewhereCountryName,
      statutoryCopiesLine,
      disabled,
      className,
    },
    acceptRef,
  ) {
    const { t } = useTranslation();
    const payrollRulesLabel = usePayrollRulesLabel();
    const toLabel = payrollRulesLabel(to);
    return (
      <Alert variant="warning" className={className} data-testid="compliance-country-change-notice">
        <AlertTitle className="leading-snug">
          {t("settings.general.countryChange.title", "Saving will switch this company's payroll rules")}
        </AlertTitle>
        {changedElsewhereCountryName && (
          <AlertDescription className="mb-1 max-w-prose font-medium" data-testid="compliance-country-change-elsewhere">
            {t("settings.general.countryChange.changedElsewhere", {
              defaultValue: "This company's country was changed to {{country}} after you opened this page.",
              country: changedElsewhereCountryName,
            })}
          </AlertDescription>
        )}
        <AlertDescription className="max-w-prose">
          {t("settings.general.countryChange.body", {
            defaultValue:
              "Statutory payroll is calculated under {{from}} today. After you save, statutory deductions, end-of-service pay and government wage files will follow {{to}}.",
            from: payrollRulesLabel(from),
            to: toLabel,
          })}
        </AlertDescription>
        {statutoryCopiesLine && (
          <AlertDescription className="mt-1 max-w-prose" data-testid="statutory-copies-line">
            {statutoryCopiesLine}
          </AlertDescription>
        )}
        {/* The shared Checkbox hides its native input, so the ring on this wrapper is what shows
            keyboard focus. */}
        <div className="-mx-1 mt-2 rounded-md p-1 focus-within:ring-2 focus-within:ring-amber-500/60">
          <Checkbox
            ref={acceptRef}
            id={COMPLIANCE_COUNTRY_CHANGE_ACCEPT_ID}
            className="rtl:space-x-reverse"
            checked={accepted}
            disabled={disabled}
            onChange={(e) => onAcceptedChange(e.target.checked)}
            label={t("settings.general.countryChange.confirm", {
              defaultValue: "I understand. Switch this company to {{to}}.",
              to: toLabel,
            })}
            error={
              refused && !accepted
                ? t(
                    "settings.general.countryChange.refused",
                    "Nothing was saved. Tick the box to confirm the new payroll rules, then save again.",
                  )
                : undefined
            }
          />
        </div>
        {onKeepCurrent && keepCountryName && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-3"
            disabled={disabled}
            onClick={onKeepCurrent}
            data-testid="compliance-country-change-keep"
          >
            {t("settings.general.countryChange.keep", { defaultValue: "Keep {{country}}", country: keepCountryName })}
          </Button>
        )}
      </Alert>
    );
  },
);

/**
 * ST-DIALOG - the same statutory-copies line on its own, for a country change that moves no payroll
 * rules and so shows no ComplianceCountryChangeNotice (both countries on the general rules, the new one
 * with catalogue rows: Lebanon to Qatar). It asks for nothing and never blocks a save, hence the info
 * tone and `role="note"`. Shared with the Operator company screen.
 */
export function StatutoryCopiesNote({ line, className }: { line: string; className?: string }) {
  return (
    <Alert variant="info" role="note" className={className} data-testid="statutory-copies-note">
      <AlertDescription className="max-w-prose" data-testid="statutory-copies-line">
        {line}
      </AlertDescription>
    </Alert>
  );
}

const COMPLIANCE_COUNTRY_OPT_IN_ACCEPT_ID = "compliance-country-optin-accept";

interface ComplianceCountryOptInNoticeProps {
  /** The rules the company runs on today. */
  from: ComplianceCountry;
  /** The rules its country calls for, which it would run on once the box is ticked and saved. */
  to: ComplianceCountry;
  accepted: boolean;
  onAcceptedChange: (accepted: boolean) => void;
  disabled?: boolean;
  className?: string;
}

/**
 * CO-879 - an OFFER, not a consent. A company created before CO-13 can sit in the UAE or Saudi
 * Arabia while its statutory payroll still runs on the general rules. Nothing on this screen changes
 * that by itself: an untouched country is not sent, so a save keeps the rules as they are. This
 * block says so and lets Hr switch by ticking the box. It never blocks a save and is not an error,
 * hence the info tone and `role="note"` (the shared Alert defaults to role="alert").
 *
 * Settings > General only. The Operator company screen does not offer it: which statutory rules a
 * company's payroll follows is that company's Hr decision.
 */
function ComplianceCountryOptInNotice({
  from,
  to,
  accepted,
  onAcceptedChange,
  disabled,
  className,
}: ComplianceCountryOptInNoticeProps) {
  const { t } = useTranslation();
  const payrollRulesLabel = usePayrollRulesLabel();
  const toLabel = payrollRulesLabel(to);
  return (
    <Alert variant="info" role="note" className={className} data-testid="compliance-country-optin-notice">
      <AlertTitle className="leading-snug">
        {t("settings.general.countryChange.optIn.title", {
          defaultValue: "Payroll does not follow {{to}} yet",
          to: toLabel,
        })}
      </AlertTitle>
      <AlertDescription className="max-w-prose">
        {t("settings.general.countryChange.optIn.body", {
          defaultValue:
            "Statutory payroll for this company is still calculated under {{from}}. If you switch, statutory deductions, end-of-service pay and government wage files will follow {{to}}. Nothing changes unless you tick the box.",
          from: payrollRulesLabel(from),
          to: toLabel,
        })}
      </AlertDescription>
      {/* The shared Checkbox hides its native input, so the ring on this wrapper is what shows
          keyboard focus. */}
      <div className="-mx-1 mt-2 rounded-md p-1 focus-within:ring-2 focus-within:ring-blue-500/60">
        <Checkbox
          id={COMPLIANCE_COUNTRY_OPT_IN_ACCEPT_ID}
          className="rtl:space-x-reverse"
          checked={accepted}
          disabled={disabled}
          onChange={(e) => onAcceptedChange(e.target.checked)}
          label={t("settings.general.countryChange.optIn.confirm", {
            defaultValue: "Switch this company to {{to}} when I save.",
            to: toLabel,
          })}
        />
      </div>
    </Alert>
  );
}

// The fields of the UAE (WPS) and KSA (employer identifier) blocks, as a company row carries them.
type StatutoryFieldsSource = Pick<
  CompanyView,
  | "molNumber"
  | "commercialRegistrationNumber"
  | "mudadEstablishmentNumber"
  | "qiwaEntityId"
>;

export default function GeneralDetails() {
  const { t } = useTranslation();
  const dispatchRedux = useDispatch();
  const complianceCountry = useComplianceCountry();
  const { userId, company: reduxCompany, role } = useSelector((s: any) => ({
    userId: s.user.userId,
    company: s.user.company,
    role: s.user.role,
  }));
  const id = reduxCompany.id;

  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [countries, setCountries] = useState<CountryView[]>([]);
  const [timeZones, setTimeZones] = useState<TimeZoneInfoView[]>([]);

  const [companyName, setCompanyName] = useState("");
  const [companyEmail, setCompanyEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [address, setAddress] = useState("");
  const [websiteLink, setWebsiteLink] = useState("");
  const [countryId, setCountryId] = useState(0);
  const [timeZone, setTimeZone] = useState("");
  const [locales, setLocales] = useState("");
  const [primaryColor, setPrimaryColor] = useState("#304EA1");
  const [employerNarrative, setEmployerNarrative] = useState("");
  const [molNumber, setMolNumber] = useState("");
  // WPS configuration (UAE)
  // Employer identifiers (KSA)
  const [commercialRegistrationNumber, setCommercialRegistrationNumber] = useState("");
  const [mudadEstablishmentNumber, setMudadEstablishmentNumber] = useState("");
  const [qiwaEntityId, setQiwaEntityId] = useState("");
  const [crFormatError, setCrFormatError] = useState<string | null>(null);
  // CO-879 - the payroll rules the SERVER held when the six statutory states above were last read
  // from it (page load, the row a save returned, or the re-read after a refusal). A block is shown,
  // and its fields are sent, only while the displayed rules equal this. When the page learns of rules
  // it did not hold (changed in another session), the states above are what the OTHER rules' block
  // held before that change, usually blank: sending them would erase what was just entered there.
  const [statutoryFieldsOf, setStatutoryFieldsOf] = useState<ComplianceCountry>(complianceCountry);
  const [planId, setPlanId] = useState(0);
  const [organizationId, setOrganizationId] = useState(0);
  const [rolesMaxUsers, setRolesMaxUsers] = useState<{[key: string]: number}>({});
  const [companyImage, setCompanyImage] = useState<File | null>(null);
  const [adminFirstName, setAdminFirstName] = useState("");
  const [adminLastName, setAdminLastName] = useState("");
  const [adminEmail, setAdminEmail] = useState("");

  const [company, setCompany] = useState<CompanyView>({} as CompanyView);
  const [roles, setRoles] = useState<string[]>([]);
  const [success, setSuccess] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  // CO-879 - what the SERVER holds for this company, as far as this screen knows: the country and
  // the payroll rules it runs on today. Loaded with the page (getCompany), replaced by every save,
  // and by a refusal that says otherwise. `compliance` is read, never derived from the country: a
  // company created before CO-13 can sit in the UAE / Saudi Arabia and still be on "None".
  const [stored, setStored] = useState<{ countryId: number; compliance: ComplianceCountry }>({
    countryId: 0,
    compliance: complianceCountry,
  });
  // The box of whichever notice is on screen (the confirmation or the opt-in; never both).
  const [countryChangeAccepted, setCountryChangeAccepted] = useState(false);
  // What the server said the change is, when it refused a save this screen thought needed no consent.
  const [serverCountryChange, setServerCountryChange] = useState<ComplianceCountryChange | null>(null);
  // That refusal showed the stored country is not the one this page loaded (changed elsewhere).
  const [countryMovedElsewhere, setCountryMovedElsewhere] = useState(false);
  // Counts blocked / refused saves; each one moves focus to the confirmation.
  const [countryChangeRefusals, setCountryChangeRefusals] = useState(0);
  const countryChangeAcceptRef = useRef<HTMLInputElement>(null);
  const saveStatusRef = useRef<HTMLDivElement>(null);
  // ST-DIALOG - the countries a change would add statutory leave copies for (getStatutoryCountriesToCopy).
  // Read once the subscription is known and never for a Free company, whose /api/Leave reads are
  // tier-locked (a 403 there raises the upgrade toast); read again after a save that moved the country.
  const { isFree: subscriptionFree, data: subscription } = useMySubscription();
  const canReadStatutoryCountries = subscription !== null && !subscriptionFree;
  const [statutoryCountriesToCopy, setStatutoryCountriesToCopy] = useState<number[]>([]);
  const loadStatutoryCountriesToCopy = async () => {
    setStatutoryCountriesToCopy(await getStatutoryCountriesToCopy());
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (canReadStatutoryCountries) loadStatutoryCountriesToCopy();
  }, [canReadStatutoryCountries]);

  useEffect(() => {
    if (countryChangeRefusals === 0) return;
    const accept = countryChangeAcceptRef.current;
    accept?.scrollIntoView({ block: "center" });
    accept?.focus({ preventScroll: true });
  }, [countryChangeRefusals]);

  // The page scrolls inside <main>, not the window, so bring the result of a save into view here.
  useEffect(() => {
    if (success || saveFailed) saveStatusRef.current?.scrollIntoView({ block: "start" });
  }, [success, saveFailed]);

  useEffect(() => {
    try {
      const palette = generatePalette(primaryColor);
      setCSSColorVariables(palette);
    } catch {}
  }, [primaryColor]);

  // Puts the six statutory states in line with a company row read from the server, and records the
  // payroll rules that row was on. `source` is a CompanyView (GET) or the Company a save returned.
  const applyStatutoryFields = (source: StatutoryFieldsSource, readUnder: ComplianceCountry) => {
    setMolNumber(source.molNumber || "");
    setCommercialRegistrationNumber(source.commercialRegistrationNumber || "");
    setMudadEstablishmentNumber(source.mudadEstablishmentNumber || "");
    setQiwaEntityId(source.qiwaEntityId || "");
    setCrFormatError(null);
    setStatutoryFieldsOf(readUnder);
  };

  // Re-reads the company for the statutory states only; every other field keeps what the user
  // typed. A failed read changes nothing: `statutoryFieldsOf` then stays behind the displayed rules,
  // so the block stays hidden and its fields are not sent (the server keeps them).
  const reloadStatutoryFields = async () => {
    try {
      const fresh = await getCompany(id);
      const freshCompliance = toComplianceCountry(fresh.payload?.complianceCountry);
      if (fresh.errors?.length === 0 && fresh.payload && freshCompliance) {
        applyStatutoryFields(fresh.payload, freshCompliance);
      }
    } catch (error) {
      console.log("error", error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    if (id) {
      const companyRes = await getCompany(id);
      if (companyRes.errors?.length === 0 && companyRes.payload) {
        const companyData = companyRes.payload;
        setCompanyName(companyData.name || "");
        setCompanyEmail(companyData.email || "");
        setPhoneNumber(companyData.phoneNumber || "");
        setAddress(companyData.address || "");
        setWebsiteLink(companyData.websiteLink || "");
        setCountryId(companyData.country?.id || 0);
        // The session's copy (Redux) is only the fallback for a payload without the field; it goes
        // stale in a tab left open while someone else switches the company.
        const loadedCompliance = toComplianceCountry(companyData.complianceCountry);
        setStored({
          countryId: companyData.country?.id || 0,
          compliance: loadedCompliance ?? complianceCountry,
        });
        if (loadedCompliance && loadedCompliance !== complianceCountry) {
          dispatchRedux(updateCompanyRules({ complianceCountry: loadedCompliance }));
        }
        setTimeZone(companyData.timeZone || "");
        setLocales(companyData.locales || "");
        setPrimaryColor(companyData.primaryColor || "#304EA1");
        setEmployerNarrative(companyData.employerNarrative || "");
        applyStatutoryFields(companyData, loadedCompliance ?? complianceCountry);
        setPlanId(companyData.subscriptionPlanView?.id || 0);
        setOrganizationId(companyData.organization?.id || 0);
        setRolesMaxUsers(companyData.rolesMaxUsers || {});
        setAdminFirstName("");
        setAdminLastName("");
        setAdminEmail("");
        setCompany(companyData);
      }
    }

    const [plansRes, countriesRes, timeZonesRes, rolesRes] = await Promise.all([
      getPlans(),
      getCountries(),
      getTimeZones(),
      getRoles()
    ]);

    if (plansRes.errors?.length === 0) setPlans(plansRes.payload);
    if (countriesRes.errors?.length === 0) setCountries(countriesRes.payload);
    if (timeZonesRes.errors?.length === 0) setTimeZones(timeZonesRes.payload);
    if (rolesRes.errors?.length === 0) setRoles(rolesRes.payload || []);
    setLoading(false);
  };

  // CO-879 - the server re-derives the payroll rules only when the request carries a country
  // DIFFERENT from the stored one, or the confirmation flag. So this screen sends the country only
  // when the user changed it or ticked a box, and an untouched country never blocks a save.
  //
  // `stored.compliance` is what the company runs on today; the selected country's ISO says what it
  // would run on. `pendingCountryChange` is the change that NEEDS consent: the user picked a country
  // on other rules, or the server refused a save (it compares against the stored row, which can
  // differ from what this page loaded; its 409 lands in `serverCountryChange` and wins).
  const selectedCountry = countries.find(c => c.id === countryId);
  const selectedCompliance = selectedCountry ? complianceCountryForIso(selectedCountry.iso) : undefined;
  const countryChanged = countryId !== stored.countryId;
  // ST-DIALOG - says the new country's statutory leave minimums will be added (spec section 5.2). Only
  // for a country the user changed; inside the confirmation when there is one, else on its own.
  const statutoryCopiesLine =
    countryChanged && selectedCountry && statutoryCountriesToCopy.includes(selectedCountry.id)
      ? t("settings.general.countryChange.statutoryCopies", {
          defaultValue: "The statutory leave minimums of {{country}} will be added to your company's Statutory Templates.",
          country: selectedCountry.name,
        })
      : undefined;
  let pendingCountryChange: { from: ComplianceCountry; to: ComplianceCountry } | null = null;
  if (serverCountryChange) {
    pendingCountryChange = {
      from: serverCountryChange.from ?? stored.compliance,
      to: serverCountryChange.to ?? selectedCompliance ?? stored.compliance,
    };
  } else if (countryChanged && selectedCompliance !== undefined && selectedCompliance !== stored.compliance) {
    pendingCountryChange = { from: stored.compliance, to: selectedCompliance };
  }
  // The optional offer (ComplianceCountryOptInNotice): the country is untouched, it calls for UAE or
  // KSA rules, and the company is still on the general ones. Null in every other case, including a
  // company already on UAE / KSA rules whose country maps elsewhere: that is not a "not yet".
  const optInTo: ComplianceCountry | null =
    !pendingCountryChange &&
    !countryChanged &&
    stored.compliance === "None" &&
    selectedCompliance !== undefined &&
    selectedCompliance !== "None"
      ? selectedCompliance
      : null;
  const switchTo: ComplianceCountry | undefined = pendingCountryChange?.to ?? optInTo ?? undefined;
  const switchingPayrollRules = switchTo !== undefined && countryChangeAccepted;
  // The statutory block on screen: the one of the rules the session shows (Redux), and only while
  // the states behind it were read under those same rules (see `statutoryFieldsOf`). What is not on
  // screen is not sent, so a save never writes a block the user could not see.
  const statutoryFieldsCurrent = complianceCountry === statutoryFieldsOf;
  const showUaeFields = statutoryFieldsCurrent && complianceCountry === "UAE";
  const showKsaFields = statutoryFieldsCurrent && complianceCountry === "KSA";

  const resetCountryChangeConsent = () => {
    setCountryChangeAccepted(false);
    setServerCountryChange(null);
    setCountryMovedElsewhere(false);
    setCountryChangeRefusals(0);
  };

  // Consent is given to ONE change, so picking another country withdraws it.
  const selectCountry = (nextCountryId: number) => {
    setCountryId(nextCountryId);
    resetCountryChangeConsent();
  };

  const handleSaveFailure = async (failure: unknown) => {
    const refusal = complianceCountryChangeRefusal(failure);
    if (refusal) {
      // The refusal names what the server holds today (country and payroll rules). That is the
      // truth, so it replaces what this page loaded: "from" is then right, and the keep button
      // appears and puts back the country the server really has. null = the company has no country.
      const serverCountryId = refusal.previousCountryId === null ? 0 : refusal.previousCountryId;
      const countryMoved = serverCountryId !== undefined && serverCountryId !== stored.countryId;
      const rulesMoved = refusal.from !== undefined && refusal.from !== stored.compliance;
      if (countryMoved) {
        setCountryMovedElsewhere(true);
      }
      setStored(prev => ({
        countryId: serverCountryId ?? prev.countryId,
        compliance: refusal.from ?? prev.compliance,
      }));
      if (refusal.from && refusal.from !== complianceCountry) {
        dispatchRedux(updateCompanyRules({ complianceCountry: refusal.from }));
      }
      setServerCountryChange(refusal);
      setCountryChangeAccepted(false);
      setCountryChangeRefusals(n => n + 1);
      // The page now holds a country or payroll rules it did not load. Whoever changed them may
      // have filled the statutory fields of the new rules too, so read those again before this page
      // can save. The caller awaits this with `loading` still set, which keeps Save disabled.
      if (countryMoved || rulesMoved) {
        await reloadStatutoryFields();
      }
      return;
    }
    console.log("error", failure);
    setSaveFailed(true);
  };

  const updateOldCompany = async () => {
    setSuccess(false);
    setSaveFailed(false);
    if (pendingCountryChange && !countryChangeAccepted) {
      // Not sent at all: the server would refuse it, and asking here keeps the form as typed.
      // Only a CHANGED country (or a server refusal) gets here; the opt-in never blocks.
      setCountryChangeRefusals(n => n + 1);
      return;
    }
    // The country goes out only when the user changed it. Left out, the server keeps the stored
    // country, and keeps the stored payroll rules too unless the consent flag is sent. The opt-in
    // sends the flag ALONE: the server then derives the rules from the country it holds, so a page
    // that is stale about the country cannot put the old country back along with the switch.
    const sendCountry = countryChanged;
    setLoading(true);
    try {
      // PUT Company/{id} is a partial update (CO-879): a field left undefined keeps its stored
      // value, a text field sent as "" is cleared. Every text field below is a string, so blanking
      // one on this screen still clears it.
      const combinedInput: CompanyUpdateInput = {
        name: companyName,
        email: companyEmail,
        // PhoneInput hands back undefined when emptied, which would mean "keep".
        phoneNumber: phoneNumber ?? "",
        address,
        websiteLink,
        countryId: sendCountry ? countryId : undefined,
        timeZone,
        locales,
        primaryColor,
        employerNarrative,
        // Statutory fields go out only for the block on screen; undefined = the server keeps them.
        molNumber: showUaeFields ? molNumber : undefined,
        commercialRegistrationNumber: showKsaFields ? commercialRegistrationNumber : undefined,
        mudadEstablishmentNumber: showKsaFields ? mudadEstablishmentNumber : undefined,
        qiwaEntityId: showKsaFields ? qiwaEntityId : undefined,
        // This screen has no plan or organization picker, so 0 here means "not loaded", never
        // "remove it": leave both out rather than send a 0 the server would act on.
        planId: planId || undefined,
        organizationId: organizationId || undefined,
        rolesMaxUsers,
        image: companyImage,
        adminFirstName,
        adminLastName,
        adminEmail,
        confirmComplianceCountryChange: switchingPayrollRules ? true : undefined,
      };
      const result = await updateCompany(id, combinedInput);
      if (result?.errors?.length) {
        await handleSaveFailure(result);
        return;
      }
      if (combinedInput.primaryColor) {
        dispatchRedux(setCompanyPrimaryColor(combinedInput.primaryColor));
      }
      // The saved row is the truth; what this request asked for is only the fallback for a payload
      // without the field.
      const saved = result?.payload;
      const savedCompliance =
        toComplianceCountry(saved?.complianceCountry) ??
        (switchingPayrollRules ? switchTo : undefined) ??
        stored.compliance;
      const savedCountryId =
        saved?.countryId ?? saved?.country?.id ?? (sendCountry ? countryId : stored.countryId);
      dispatchRedux(updateCompanyRules({ complianceCountry: savedCompliance }));
      setStored({ countryId: savedCountryId, compliance: savedCompliance });
      // An untouched country was not sent. If it was changed elsewhere since this page loaded, the
      // server kept THAT one: show it, or the next save would send the old country as a change.
      if (!sendCountry) setCountryId(savedCountryId);
      // The copies of the country just saved now exist, so it no longer adds any.
      if (savedCountryId !== stored.countryId && canReadStatutoryCountries) loadStatutoryCountriesToCopy();
      // The saved row also says what every statutory field holds now, including the ones this
      // request left out: the block of rules just switched to, or of rules another session moved
      // the company to. `wpsEmployeeThreshold` is a number on every Company row, so its presence
      // tells a full row from a payload that cannot be trusted for the other five.
      if (saved && typeof saved.wpsEmployeeThreshold === "number") {
        applyStatutoryFields(saved, savedCompliance);
      } else if (savedCompliance !== statutoryFieldsOf || savedCountryId !== stored.countryId) {
        await reloadStatutoryFields();
      }
      resetCountryChangeConsent();
      setSuccess(true);
    } catch (error) {
      await handleSaveFailure(error);
    } finally {
      setLoading(false);
    }
  };

  const validateInputs = () => {
    if (countryId == null || countryId === 0 || companyName == null || companyName === "") {
      return false;
    }
    // CR must be 10 digits when provided (KSA Mudad requirement).
    // Checked only while the field is on screen: a hidden one is not sent, and its error would not show.
    if (showKsaFields && commercialRegistrationNumber && !/^\d{10}$/.test(commercialRegistrationNumber)) {
      setCrFormatError(t('settings.compliance.commercialRegistrationFormatInvalid', 'Commercial Registration must be 10 digits'));
      return false;
    }
    setCrFormatError(null);
    return true;
  };

  const addOrUpdate = async () => {
    if (validateInputs()) await updateOldCompany();
  };

  const { rolesUsersAvailable } = company;

  const countryOptions = countries.map(c => ({ value: c.id, label: c.name }));
  const timeZoneOptions = Array.from(
    new Map(timeZones.map(tz => [tz.id, { value: tz.id, label: tz.displayName }])).values()
  );
  const storedCountryName = countries.find(c => c.id === stored.countryId)?.name;
  // Names the outcome once the user has agreed to it (ui-standards: destructive actions).
  const saveLabel = switchingPayrollRules
    ? t("settings.general.countryChange.save", "Save and switch payroll rules")
    : t("Save Changes");

  return (
    <PageLayout
      title={t("Company Settings")}
      description={t("Manage your company information and preferences")}
      icon={Building2}
      accentColor="slate"
      headerActions={(
        <Button
          variant={switchingPayrollRules ? "destructive" : "outline"}
          disabled={loading}
          onClick={addOrUpdate}
          iconName="Save"
        >
          {loading ? <Spinner size="sm" /> : saveLabel}
        </Button>
      )}
    >
      <div ref={saveStatusRef}>
        {success && (
          <div role="alert" data-testid="company-save-success" className="rounded-lg border border-emerald-500/50 bg-emerald-500/10 p-4 text-sm text-emerald-700 dark:text-emerald-400 mb-4 flex items-center justify-between">
            <span>{t("Company Updated Successfully")}</span>
            <button onClick={() => setSuccess(false)} className="hover:opacity-80 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm">&times;</button>
          </div>
        )}
        {saveFailed && (
          <Alert
            variant="error"
            className="mb-4 text-sm"
            onClose={() => setSaveFailed(false)}
            closeLabel={t("Close")}
            data-testid="company-save-failed"
          >
            {t("settings.general.saveFailed", "The company details were not saved. Check the fields and try again.")}
          </Alert>
        )}
      </div>

      <div className="section grid-auto-fit">
        {role === Roles.Operator && (
          <div className="col-span-full">
            <Input
              label={t("Company Name")}
              required
              placeholder={t("Enter company name")}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCompanyName(e.target.value)}
              value={companyName}
            />
          </div>
        )}

        <Input
          label={t("Email")}
          type="email"
          placeholder={t("Enter email address")}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCompanyEmail(e.target.value)}
          value={companyEmail}
        />

        <Select
          label={t("Country")}
          required
          options={countryOptions}
          value={countryId}
          onChange={(val: any) => selectCountry(val)}
          searchable
          placeholder={t("Select country")}
          data-testid="company-country"
        />

        {pendingCountryChange && (
          <ComplianceCountryChangeNotice
            ref={countryChangeAcceptRef}
            className="col-span-full"
            from={pendingCountryChange.from}
            to={pendingCountryChange.to}
            accepted={countryChangeAccepted}
            onAcceptedChange={setCountryChangeAccepted}
            refused={countryChangeRefusals > 0}
            onKeepCurrent={stored.countryId && countryChanged ? () => selectCountry(stored.countryId) : undefined}
            keepCountryName={storedCountryName}
            changedElsewhereCountryName={countryMovedElsewhere ? storedCountryName : undefined}
            statutoryCopiesLine={statutoryCopiesLine}
            disabled={loading}
          />
        )}

        {!pendingCountryChange && statutoryCopiesLine && (
          <StatutoryCopiesNote className="col-span-full" line={statutoryCopiesLine} />
        )}

        {optInTo && (
          <ComplianceCountryOptInNotice
            className="col-span-full"
            from={stored.compliance}
            to={optInTo}
            accepted={countryChangeAccepted}
            onAcceptedChange={setCountryChangeAccepted}
            disabled={loading}
          />
        )}

        <div>
          <label className="text-sm font-medium text-foreground mb-2 block">{t('Phone Number')}</label>
          <PhoneInput
            international
            placeholder={t("Enter phone number")}
            className="phone-input-reset"
            value={phoneNumber}
            onChange={(value: any) => setPhoneNumber(value ?? "")}
          />
        </div>

        <Input
          label={t("Address")}
          placeholder={t("Enter address")}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAddress(e.target.value)}
          value={address}
        />

        <Input
          label={t("Website")}
          placeholder={t("Enter website URL")}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setWebsiteLink(e.target.value)}
          value={websiteLink}
        />

        <Select
          label={t("TimeZone")}
          options={timeZoneOptions}
          value={timeZone}
          onChange={(val: any) => setTimeZone(val)}
          searchable
          placeholder={t("Select timezone")}
        />

        <Input
          label={t("Employer Narrative")}
          placeholder={t("Enter employer narrative")}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmployerNarrative(e.target.value)}
          value={employerNarrative}
        />

        {/* WPS Configuration (UAE compliance tenants) */}
        {showUaeFields && (
          <Input
            label={t("MOL Number")}
            placeholder={t("Enter MOL number")}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMolNumber(e.target.value)}
            value={molNumber}
          />
        )}

        {/* Employer Identifiers (KSA compliance tenants) */}
        {showKsaFields && (
          <>
            <div>
              <Input
                label={t('settings.compliance.commercialRegistration', 'Commercial Registration')}
                placeholder={t('settings.compliance.commercialRegistrationPlaceholder', '10-digit CR number')}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setCommercialRegistrationNumber(e.target.value); setCrFormatError(null); }}
                value={commercialRegistrationNumber}
                aria-invalid={!!crFormatError}
              />
              {crFormatError && <p className="text-xs text-red-600 mt-1">{crFormatError}</p>}
            </div>
            <Input
              label={t('settings.compliance.mudadEstablishmentNumber', 'Mudad Establishment Number')}
              placeholder={t('settings.compliance.mudadEstablishmentNumberPlaceholder', 'Issued by Mudad portal')}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMudadEstablishmentNumber(e.target.value)}
              value={mudadEstablishmentNumber}
            />
            <Input
              label={t('settings.compliance.qiwaEntityId', 'Qiwa Entity ID')}
              placeholder={t('settings.compliance.qiwaEntityIdPlaceholder', 'Issued by Qiwa')}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQiwaEntityId(e.target.value)}
              value={qiwaEntityId}
            />
          </>
        )}

        {role === Roles.Operator && (
          <Input
            label={t("Locales")}
            required
            placeholder={t("Enter locales")}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLocales(e.target.value)}
            value={locales}
          />
        )}
      </div>

      <div className="flex flex-wrap gap-6 mt-8 items-start mb-1">
        <div className="flex-1 min-w-[400px]">
          <div className="section p-4">
            <h2 className="text-sm font-semibold text-primary mb-2">{t("Licensed Users")}</h2>
            <div className="flex flex-row flex-wrap gap-2">
              {roles.map((r) => (
                <div key={r} className="flex flex-col items-center flex-1 min-w-[200px] p-3 bg-card rounded-md border border-border">
                  <h3 className="text-sm font-semibold text-foreground mb-4">{r}</h3>
                  <div className="flex items-center gap-2">
                    {id && (
                      <span className="text-sm font-medium text-muted-foreground">
                        {`${rolesUsersAvailable?.[r] ?? 0} /`}
                      </span>
                    )}
                    {role === Roles.Operator ? (
                      <input
                        type="number"
                        min="0"
                        className="w-20 h-8 rounded-md border border-input bg-background px-2 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                        onChange={(ev) => {
                          const value = parseInt(ev.target.value || '0');
                          setRolesMaxUsers(prev => ({ ...prev, [r]: value }));
                        }}
                        value={rolesMaxUsers[r] as any}
                      />
                    ) : (
                      <span className="inline-flex items-center rounded-full bg-blue-100 text-blue-800 px-2 py-0.5 text-xs font-medium">
                        {rolesMaxUsers[r]}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>

      <hr className="border-border my-4" />

      <div className="flex justify-end mt-3">
        <Button
          variant={switchingPayrollRules ? "destructive" : "default"}
          disabled={loading}
          onClick={addOrUpdate}
          data-testid="company-save"
        >
          {loading ? <Spinner size="sm" /> : saveLabel}
        </Button>
      </div>
    </PageLayout>
  );
}
